import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import type { BetterDocCareProviders } from "@/modules/patients/detail/better-doc/types"

interface UseBetterDocSuggestionsStoreProps {
  betterDocMap: Map<string, BetterDocCareProviders | undefined>
  setBetterDocSuggestions: (id: string, suggestions?: BetterDocCareProviders) => void
  getBetterDocSuggestions: (id?: string) => BetterDocCareProviders | undefined
  isBetterDocEmpty: (id?: string) => boolean | undefined
  onReset: (id?: string) => void
}

const createBetterDocSuggestionsStore = () => {
  return create<UseBetterDocSuggestionsStoreProps>()((set, get) => ({
    betterDocMap: new Map(),
    setBetterDocSuggestions: (id, betterDocSuggestions) => {
      const betterDocMap = new Map(get().betterDocMap)
      betterDocMap.set(id, betterDocSuggestions)
      set({ betterDocMap })
    },
    getBetterDocSuggestions: (id) => (id ? get().betterDocMap.get(id) : undefined),
    isBetterDocEmpty: (id) => {
      if (!id) {
        return false
      }

      const betterDocSuggestions = get().betterDocMap.get(id)
      return betterDocSuggestions && betterDocSuggestions.length === 0
    },
    onReset: (id) => {
      if (!id) {
        return
      }
      const betterDocMap = new Map(get().betterDocMap)
      betterDocMap.set(id, undefined)
      set({ betterDocMap })
    },
  }))
}

type BetterDocSuggestionsStoreType = ReturnType<typeof createBetterDocSuggestionsStore>

const BetterDocSuggestionsStoreContext = createContext<BetterDocSuggestionsStoreType | null>(null)

type BetterDocSuggestionsStoreProviderProps = React.PropsWithChildren

export const BetterDocSuggestionsStoreProvider = ({ children }: BetterDocSuggestionsStoreProviderProps) => {
  const storeRef = useRef<BetterDocSuggestionsStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createBetterDocSuggestionsStore()
  }
  return (
    <BetterDocSuggestionsStoreContext.Provider value={storeRef.current}>
      {children}
    </BetterDocSuggestionsStoreContext.Provider>
  )
}

export function useBetterDocSuggestionsStoreContext<T>(selector: (state: UseBetterDocSuggestionsStoreProps) => T): T {
  const store = useContext(BetterDocSuggestionsStoreContext)
  if (!store) throw new Error("Missing BetterDocSuggestionsStoreProvider in the tree")
  return useStore(store, selector)
}
